import { Box, Radio, Stack, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";

import {
  getVoiceReactionOptions,
  getVoiceReactionPacingRhythmOptions,
  getVoiceReactionVolumeEnergyOptions,
} from "Forms/Utils/EnumOptions";
import type {
  VoiceReactionPacingRhythmTag,
  VoiceReactionTag,
  VoiceReactionVolumeEnergyTag,
  VoiceSpan,
} from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import type { VoiceSpanReactionPatch } from "Projects/Components/ScriptEditor/ScriptEditorContext";
import { BlModal } from "Shared/Dialogs/BlModal";
import { BlInput } from "Shared/Inputs/BlInput";
import type { BlOption } from "Shared/Inputs/BlOption";

interface ReactionEditModalProps {
  isOpen: boolean;
  initialValue: VoiceSpan | null;
  onConfirm: (patch: VoiceSpanReactionPatch) => void;
  onCancel: () => void;
}

/**
 * Modal for creating or editing a reaction span. Selecting any enum value
 * clears the other categories + the custom text; typing in the custom input
 * clears all enum selections (matches the BE mutual-exclusion rule).
 */
export const ReactionEditModal = ({
  isOpen,
  initialValue,
  onConfirm,
  onCancel,
}: ReactionEditModalProps): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();

  const [reaction, setReaction] = useState<VoiceReactionTag | null>(null);
  const [volume, setVolume] = useState<VoiceReactionVolumeEnergyTag | null>(
    null,
  );
  const [pacing, setPacing] = useState<VoiceReactionPacingRhythmTag | null>(
    null,
  );
  const [custom, setCustom] = useState<string>("");

  // Sync modal state with the span passed in every time the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setReaction((initialValue?.reaction as VoiceReactionTag | null) ?? null);
    setVolume(
      (initialValue?.reactionVolumeEnergy as VoiceReactionVolumeEnergyTag | null) ??
        null,
    );
    setPacing(
      (initialValue?.reactionPacingRhythm as VoiceReactionPacingRhythmTag | null) ??
        null,
    );
    setCustom(initialValue?.customReaction ?? "");
  }, [isOpen, initialValue]);

  const pickReaction = (v: VoiceReactionTag | null) => {
    setReaction(v);
    setVolume(null);
    setPacing(null);
    setCustom("");
  };
  const pickVolume = (v: VoiceReactionVolumeEnergyTag | null) => {
    setReaction(null);
    setVolume(v);
    setPacing(null);
    setCustom("");
  };
  const pickPacing = (v: VoiceReactionPacingRhythmTag | null) => {
    setReaction(null);
    setVolume(null);
    setPacing(v);
    setCustom("");
  };
  const pickCustom = (v: string) => {
    setReaction(null);
    setVolume(null);
    setPacing(null);
    setCustom(v);
  };

  const renderRadioGroup = <T extends string>(
    heading: string,
    options: BlOption[],
    value: T | null,
    onChange: (v: T | null) => void,
  ) => (
    <Box>
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: 600,
          color: theme.palette.text.secondary,
          mb: 0.5,
        }}
      >
        {heading}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          rowGap: 0.25,
        }}
      >
        {options.map((o) => (
          <Box
            key={String(o.value)}
            onClick={() =>
              onChange((value === o.value ? null : o.value) as T | null)
            }
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
            }}
          >
            <Radio size="small" checked={value === o.value} sx={{ p: 0.25 }} />
            <Typography sx={{ fontSize: "13px" }}>{o.label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );

  const handleConfirm = () => {
    const trimmed = custom.trim();
    onConfirm({
      reaction,
      reactionVolumeEnergy: volume,
      reactionPacingRhythm: pacing,
      customReaction: trimmed.length === 0 ? null : trimmed,
    });
  };

  return (
    <BlModal
      isOpen={isOpen}
      title={t(Resources.JobDetail.Reaction.Heading)}
      onCancel={onCancel}
      onConfirm={handleConfirm}
      cancelButtonText={t(Resources.Common.Cancel)}
      confirmButtonText={t(Resources.Common.Save)}
      maxWidth="520px"
    >
      <Stack spacing={Spacing.M}>
        {renderRadioGroup<VoiceReactionTag>(
          t(Resources.JobDetail.Reaction.CategoryReactions),
          getVoiceReactionOptions(),
          reaction,
          pickReaction,
        )}
        {renderRadioGroup<VoiceReactionVolumeEnergyTag>(
          t(Resources.JobDetail.Reaction.CategoryVolumeEnergy),
          getVoiceReactionVolumeEnergyOptions(),
          volume,
          pickVolume,
        )}
        {renderRadioGroup<VoiceReactionPacingRhythmTag>(
          t(Resources.JobDetail.Reaction.CategoryPacing),
          getVoiceReactionPacingRhythmOptions(),
          pacing,
          pickPacing,
        )}

        <Box>
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              color: theme.palette.text.secondary,
              mb: 0.5,
            }}
          >
            {t(Resources.JobDetail.Reaction.Custom)}
          </Typography>
          <BlInput
            name="customReaction"
            size="small"
            value={custom}
            placeholder={t(Resources.JobDetail.Reaction.CustomPlaceholder)}
            onChange={(e) => pickCustom(e.target.value)}
          />
        </Box>
      </Stack>
    </BlModal>
  );
};
